var __extends = (this && this.__extends) || (function () {
    var extendStatics = function (d, b) {
        extendStatics = Object.setPrototypeOf ||
            ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
            function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
        return extendStatics(d, b);
    };
    return function (d, b) {
        if (typeof b !== "function" && b !== null)
            throw new TypeError("Class extends value " + String(b) + " is not a constructor or null");
        extendStatics(d, b);
        function __() { this.constructor = d; }
        d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
    };
})();
import Dinero from 'dinero.js';
import { OrderItem } from './OrderItem';
import { OrderShippingMethod } from './OrderShippingMethod';
import { OrderPrices } from './OrderPrices';
import { ShippingRate } from './ShippingRate';
import { PriceDetails } from './Purchase';
import dayjs from 'dayjs';
export var OrderStatus;
(function (OrderStatus) {
    OrderStatus["PendingPayment"] = "PendingPayment";
    OrderStatus["ConfirmedByClient"] = "ConfirmedByClient";
    OrderStatus["Scheduled"] = "Scheduled";
    OrderStatus["PendingProcessing"] = "PendingProcessing";
    OrderStatus["Processing"] = "Processing";
    OrderStatus["OnHold"] = "OnHold";
    OrderStatus["Cancelled"] = "Cancelled";
    OrderStatus["Completed"] = "Completed";
})(OrderStatus || (OrderStatus = {}));
export var OrderStatusReadable = [
    { value: OrderStatus.PendingPayment, label: 'Pending Payment' },
    { value: OrderStatus.ConfirmedByClient, label: 'Payment TBC' },
    { value: OrderStatus.Scheduled, label: 'Scheduled' },
    { value: OrderStatus.PendingProcessing, label: 'Pending Processing' },
    { value: OrderStatus.Processing, label: 'Processing' },
    { value: OrderStatus.OnHold, label: 'Blocked' },
    { value: OrderStatus.Completed, label: 'Completed' },
    { value: OrderStatus.Cancelled, label: 'Cancelled' },
];
export var DistributionType;
(function (DistributionType) {
    DistributionType[DistributionType["Digital"] = 0] = "Digital";
    DistributionType[DistributionType["Print"] = 1] = "Print";
})(DistributionType || (DistributionType = {}));
var Order = /** @class */ (function () {
    function Order() {
        this.status = OrderStatus.PendingPayment;
        this.orderItems = [];
    }
    Order.prototype.calculatePrices = function () {
        var _a, _b;
        var newShippingCost = this.getShippingCost();
        var newTaxesPaid = this.getTaxesPaid();
        var newTotal = this.getTotal();
        if (this.areEqual(newShippingCost, (_a = this.priceDetails) === null || _a === void 0 ? void 0 : _a.shippingCost) &&
            this.areEqual((_b = this.priceDetails) === null || _b === void 0 ? void 0 : _b.taxes, newTaxesPaid) &&
            this.areEqual(newTotal, this.totalPrice)) {
            return this;
        }
        return Object.assign(new Order(), this, {
            priceDetails: Object.assign(new OrderPrices(), this.priceDetails, {
                taxesPaid: newTaxesPaid,
                shippingCost: newShippingCost
            }),
            totalPrice: newTotal
        });
    };
    Order.prototype.areEqual = function (a, b) {
        if (a === b) {
            return true;
        }
        if (b) {
            return a === null || a === void 0 ? void 0 : a.equalsTo(b);
        }
        return false;
    };
    Order.prototype.getTaxesPaid = function () {
        return Dinero({ amount: 0, currency: 'USD' });
    };
    Order.prototype.getShippingAddress = function () {
        var _a, _b;
        return (_a = this.shippingAddress) !== null && _a !== void 0 ? _a : (_b = this.purchase) === null || _b === void 0 ? void 0 : _b.shippingAddress;
    };
    Order.prototype.getSubTotal = function () {
        var total = Dinero({ amount: 0, currency: 'USD' });
        this.orderItems.forEach(function (x) {
            if (x === null || x === void 0 ? void 0 : x.price) {
                total = total.add(x.price);
            }
        });
        return total;
    };
    Order.prototype.getTotal = function () {
        var total = this.getSubTotal();
        var shippingCost = this.getShippingCost();
        if (shippingCost) {
            total = total.add(shippingCost);
        }
        return total;
    };
    Order.prototype.getShippingCost = function () {
        if (this.shippingRates && this.shippingMethod) {
            var mailClass_1 = this.shippingMethod.mailClass;
            var selShippingRate = this.shippingRates.find(function (x) { return (x === null || x === void 0 ? void 0 : x.mailClass) === mailClass_1; });
            if (selShippingRate) {
                return selShippingRate.totalPrice;
            }
        }
        return null;
    };
    Order.prototype.shouldHaveShippingRates = function () {
        return this.orderItems.some(function (x) { return x.distributionType === DistributionType.Print; });
    };
    Order.prototype.hasValidShippingRates = function () {
        var _a, _b;
        return ((_b = (_a = this.shippingRates) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0;
    };
    Order.prototype.hasValidShippingAddressInfo = function () {
        var address = this.getShippingAddress();
        if (address && address.country && address.postalCode) {
            return true;
        }
        return false;
    };
    Order.prototype.getSummedCartItems = function () {
        var countedCartItems = [];
        this.orderItems.forEach(function (item) {
            var existingItem = countedCartItems.find(function (cci) {
                return (cci.orderItem.product.id === item.product.id) && (cci.orderItem.variant.id === item.variant.id) && (cci.orderItem.price.toUnit() === item.price.toUnit());
            });
            if (existingItem != null) {
                existingItem.amount = existingItem.amount + 1;
                return;
            }
            var newItem = {
                amount: 1,
                orderItem: item
            };
            countedCartItems.push(newItem);
        });
        // console.log(`Order has ${countedCartItems.length} distinct items..`);
        return countedCartItems;
    };
    ;
    Order.prototype.setShippingMethod = function (mailClass) {
        var method = this.createShippingMethod(mailClass);
        if (method === null && this.shippingMethod === null) {
            return this;
        }
        var newOrder = Object.assign(new Order(), this, {
            shippingMethod: method
        });
        return newOrder.calculatePrices();
    };
    Order.prototype.split = function () {
        var _a;
        var ordersByDate = [];
        var _loop_1 = function (i) {
            var oi = this_1.orderItems[i];
            var obd = ordersByDate.find(function (x) { var _a; return x[0] === ((_a = oi.variant) === null || _a === void 0 ? void 0 : _a.scheduleDate); });
            if (!obd) {
                obd = [
                    (_a = oi.variant) === null || _a === void 0 ? void 0 : _a.scheduleDate,
                    Object.assign(new Order(), this_1, {
                        orderItems: []
                    })
                ];
                ordersByDate.push(obd);
            }
            obd[1].orderItems.push(oi);
        };
        var this_1 = this;
        for (var i = 0; i < this.orderItems.length; i++) {
            _loop_1(i);
        }
        return ordersByDate.map(function (x) { return x[1]; });
    };
    Order.prototype.getDispatchDate = function () {
        var allDates = this.orderItems.map(function (x) { return x.variant.scheduleDate; });
        allDates = allDates.filter(function (x) { return x !== null; });
        var latestDate = new Date();
        for (var i = 0; i < allDates.length; i++) {
            console.log("Date: ".concat(latestDate, " vs ").concat(allDates[i]));
            if (allDates[i].getTime() > latestDate.getTime()) {
                latestDate = allDates[i];
            }
        }
        return latestDate;
    };
    Order.prototype.createShippingMethod = function (mailClass) {
        if (!mailClass) {
            return null;
        }
        var rate = this.shippingRates.find(function (x) { return x.mailClass === mailClass; });
        if (rate === undefined) {
            return null;
        }
        var method = new OrderShippingMethod();
        method.mailClass = rate.mailClass;
        method.mailServiceName = rate.mailService;
        return method;
    };
    Order.prototype.toApi = function () {
        var _a, _b, _c, _d;
        var dto = Object.assign({}, this, {
            totalPrice: (_a = this.totalPrice) === null || _a === void 0 ? void 0 : _a.getAmount(),
            orderItems: (_b = this.orderItems) === null || _b === void 0 ? void 0 : _b.map(function (x) { return x.toApi(); }),
            shippingRates: (_c = this.shippingRates) === null || _c === void 0 ? void 0 : _c.map(function (x) { return x.toApi(); }),
            priceDetails: (_d = this.priceDetails) === null || _d === void 0 ? void 0 : _d.toApi()
        });
        delete dto.purchase;
        return dto;
    };
    Order.createFromApi = function (dto) {
        var _a, _b;
        var res = Object.assign(new Order(), dto, {
            totalPrice: Dinero({ amount: Math.round(dto.totalPrice * 10000), currency: "USD", precision: 4 }),
            orderItems: dto.orderItems.map(function (x) { return OrderItem.createFromApi(x); }),
            priceDetails: PriceDetails.createFromApi(dto.priceDetails)
        });
        if (dto.shippingRates) {
            res.shippingRates = dto.shippingRates.map(function (x) { return ShippingRate.createFromApi(x); });
        }
        if (!dto.shippingMethod && ((_b = (_a = res === null || res === void 0 ? void 0 : res.shippingRates) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0) > 0) {
            res.shippingMethod = res.createShippingMethod(res.shippingRates[0].mailClass);
        }
        console.log(res);
        return res;
    };
    return Order;
}());
export { Order };
// OrderRefund.ts
var RefundReason = /** @class */ (function () {
    function RefundReason() {
    }
    return RefundReason;
}());
export { RefundReason };
var OrderRefund = /** @class */ (function () {
    function OrderRefund() {
        this.refundReason = new RefundReason();
        this.orderRefundItems = [];
    }
    OrderRefund.createFromApi = function (dto) {
        var _a;
        if (dto == null) {
            return null;
        }
        // Empty Object + dto + anything mappings from base
        return Object.assign(new OrderRefund(), dto, {
            shippingAmount: Dinero({ amount: Math.round(((_a = dto === null || dto === void 0 ? void 0 : dto.shippingAmount) !== null && _a !== void 0 ? _a : 0) * 1000), currency: "USD", precision: 4 })
        });
    };
    OrderRefund.prototype.toApi = function () {
        return Object.assign({}, this, {
            shippingAmount: this.shippingAmount.toUnit()
        });
    };
    return OrderRefund;
}());
export { OrderRefund };
var OrderRefundItem = /** @class */ (function () {
    function OrderRefundItem() {
    }
    return OrderRefundItem;
}());
export { OrderRefundItem };
// Used for displaying more information for the dashboard.
var OrderAdmin = /** @class */ (function (_super) {
    __extends(OrderAdmin, _super);
    function OrderAdmin() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    OrderAdmin.createFromApi = function (dto) {
        // Empty Object + dto + anything mappings from base
        var orderRefund = OrderRefund.createFromApi(dto.orderRefund);
        console.debug("ORDERREFUND", orderRefund);
        return Object.assign(new OrderAdmin(), dto, Order.createFromApi(dto), {
            orderRefund: OrderRefund.createFromApi(dto.orderRefund)
        });
    };
    OrderAdmin.prototype.toApi = function () {
        var _a;
        return Object.assign(_super.prototype.toApi.call(this), this, {
            orderRefund: (_a = this.orderRefund) === null || _a === void 0 ? void 0 : _a.toApi()
        });
    };
    return OrderAdmin;
}(Order));
export { OrderAdmin };
//Using this for the general attached note abstract class in the backend. should be renamed to AttachedNote with an extensions for OrderNote and UserNote
var OrderNote = /** @class */ (function () {
    function OrderNote() {
    }
    OrderNote.prototype.toApi = function () {
        return Object.assign({}, this, {
            timeCreated: this.timeCreated.toISOString()
        });
    };
    OrderNote.createFromApi = function (dto) {
        if (dto == null) {
            return null;
        }
        // Empty Object + dto + anything mappings from base
        return Object.assign(new OrderNote(), dto, {
            timeCreated: dto.timeCreated ? dayjs(dto.timeCreated + 'Z') : null
        });
    };
    return OrderNote;
}());
export { OrderNote };
var OrderRequest = /** @class */ (function (_super) {
    __extends(OrderRequest, _super);
    function OrderRequest() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    return OrderRequest;
}(Order));
export { OrderRequest };
// .net: InkyAPI.ApiModels.ApiOrderSummary
var OrderSummary = /** @class */ (function () {
    function OrderSummary() {
    }
    return OrderSummary;
}());
export { OrderSummary };
var OrderSummaryAdmin = /** @class */ (function (_super) {
    __extends(OrderSummaryAdmin, _super);
    function OrderSummaryAdmin() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    return OrderSummaryAdmin;
}(OrderSummary));
export { OrderSummaryAdmin };
var OrdersCount = /** @class */ (function () {
    function OrdersCount() {
    }
    return OrdersCount;
}());
export { OrdersCount };
